<template>
  <div></div>
</template>

<script setup lang="ts">
import { toRefs, watch } from 'vue'
import { useDataSources } from '@/composables/useDataSources'
import { useDataFetcher } from '@/composables/useDataFetcher'
import { FetchType } from '@/fetches'
import { DataSource } from './data-section-types'
import {
  FromOfTransform,
  OptionsOfTransform,
  ToOfTransform,
  TransformId,
  transforms
} from '@/transforms'
import { useI18n } from 'vue-i18n'

type Props = {
  fetch: FetchType
  dataSources: DataSource<'fetch'>[]
}

const props = defineProps<Props>()

const { fetch, dataSources } = toRefs(props)
const { data, isLoading, error } = useDataFetcher(fetch)

const { setDataSource } = useDataSources()

const { getLocaleMessage } = useI18n()

const transform = <Id extends TransformId>(
  id: Id,
  options: OptionsOfTransform<Id>
): ToOfTransform<Id> => {
  if (id in transforms) {
    const t = transforms[id]
    // @ts-expect-error - type system not smart enough to infer that the type matches for options
    return t({ from: data.value as FromOfTransform<Id>, isLoading: isLoading.value }, options)
  } else {
    throw new Error(`Unknown transform ${id}`)
  }
}

const transformData = (
  dataSource: DataSource<'fetch'>,
  d: (typeof data)['value']
): (typeof data)['value'] => {
  if ('path' in dataSource && dataSource.path) {
    const id = 'path'
    const options = { path: dataSource.path }
    return transform(id, { ...options, getLocaleMessage })
  } else if ('transform' in dataSource && dataSource.transform) {
    const id =
      typeof dataSource.transform === 'string' ? dataSource.transform : dataSource.transform.id
    const options =
      typeof dataSource.transform === 'string' ? {} : { ...dataSource.transform, id: undefined }
    return transform(id, { ...options, getLocaleMessage })
  }
  return d
}

watch(
  [data, isLoading],
  () => {
    for (const dataSource of dataSources.value) {
      const { id } = dataSource
      setDataSource({
        id,
        data: transformData(dataSource, data.value),
        error: error.value,
        isLoading: isLoading.value
      })
    }
  },
  { immediate: true }
)
</script>
